import { useLoadingText } from '../../hooks/useLoadingText'
import DriverInstructionsBadge from '../driver/DriverInstructionsBadge'
import { useCallback, useEffect, useRef, useState, type ComponentType, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { ArrowDown, ChevronLeft, ChevronRight, CircleAlert, CircleX, FileText, LoaderCircle, MapPin, Menu, Navigation, Truck, X } from 'lucide-react'
import { useMobileMenu } from '../layout/MobileMenuContext'
import { isPageScrollLocked, usePageScrollLock } from '../../hooks/usePageScrollLock'
import { translateStatus, type Shipment } from '../../lib/api'
import { getEffectiveShipmentStatus, getLastTrackingLabel, getManagerStatusLabel, getShipmentAttention, getRouteLabel, type AttentionItem } from '../../lib/managerOperational'
import { formatOrderCompactDate } from '../../lib/orderCabinet'
import { pickWithOrderFallback } from '../../lib/fromOrder'
import { parseRouteData } from '../../lib/routeData'
import { vehicleTypeShortLabel } from '../../lib/vehicleTypes'
import './mobile-web.css'

export type MobileTone = 'accent' | 'info' | 'success' | 'warning' | 'danger' | 'neutral'

export type MobileOrderView = {
  id: string
  title: string
  route: string
  status: string
  statusLabel: string
  tone: MobileTone
  timeLabel: string
  timeCaption: string
  driverLabel: string
  managerLabel?: string
  managerName?: string
  managerCompany?: string
  vehicleLabel: string
  vehicleSpecified: boolean
  documentsLabel?: string
  documentsCount?: number
  canReadInstructions: boolean
  managerView?: boolean
  warningLabel?: string
  lastLocationLabel?: string
  raw: Shipment
  trackingCode?: string
  pendingAcceptance?: boolean
  // Driver view: the driver accepted the trip and has not pressed «Начать рейс» yet.
  awaitingStart?: boolean
}

// isAwaitingTripStart: the driver's own accepted shipment whose trip has not started — the raw
// status stays «created» until «Начать рейс» (the server's effective «accepted»).
export function isAwaitingTripStart(shipment: Pick<Shipment, 'status'>): boolean {
  return shipment.status === 'created' || shipment.status === 'pending' || shipment.status === 'assigned'
}

function documentsLabel(count: number) {
  if (count === 0) return 'Документы не прикреплены'
  const remainder = count % 100
  const lastDigit = count % 10
  const noun = remainder >= 11 && remainder <= 14
    ? 'документов'
    : lastDigit === 1
      ? 'документ'
      : lastDigit >= 2 && lastDigit <= 4
        ? 'документа'
        : 'документов'
  return `${count} ${noun}`
}

export function shipmentToMobileOrder(
  shipment: Shipment,
  options: {
    attention?: AttentionItem
    documentCount?: number
    trackingCode?: string
    pendingAcceptance?: boolean
    assignmentStatus?: string
    managerView?: boolean
    now?: Date
  } = {},
): MobileOrderView {
  const routeData = parseRouteData(shipment.route_json)
  // The API exposes effective_status for the manager presentation only. Driver screens keep the
  // shipment state separate from their invitation state.
  const status = options.managerView ? getEffectiveShipmentStatus(shipment) : shipment.status
  const assignment = shipment.assignment
  const acceptedDriver = assignment?.status === 'accepted' ? assignment.driver_name?.trim() : ''
  const driver = acceptedDriver || routeData?.driver_name?.trim() || ''
  // `company` is the carrier named in the order, not the manager's company.
  const managerCompany = routeData?.manager_company?.trim() || ''
  const managerName = routeData?.manager_name?.trim() || ''
  const managerLabel = managerCompany || managerName
  const realPlate = assignment?.status === 'accepted' ? assignment.vehicle_plate?.trim() : ''
  const realType = assignment?.status === 'accepted' ? assignment.vehicle_type?.trim() : ''
  // vehicleTypeShortLabel('') is «—» (truthy): without the guard a card with no type read «А123ВС777 · —».
  const typeCode = realType || routeData?.vehicle_type?.trim()
  const vehicleType = typeCode ? vehicleTypeShortLabel(typeCode) : ''
  // The accepted driver's plate, else the order's — marked once someone accepted (lib/fromOrder.ts).
  const plate = pickWithOrderFallback(assignment?.status === 'accepted', realPlate, routeData?.vehicle_number)
  const vehicleSpecified = Boolean(plate || vehicleType)
  const date = routeData?.loading_date || shipment.created_at
  const pendingAcceptance = Boolean(options.pendingAcceptance)
  const assignmentStatus = options.assignmentStatus
  const documentCount = options.documentCount ?? shipment.document_count
  const attention = options.attention ?? (options.managerView ? getShipmentAttention(shipment, options.now ?? new Date()) : undefined)
  // The driver's own trip, accepted and not started: the card offers «Начать рейс».
  const awaitingStart = !options.managerView && !pendingAcceptance && assignmentStatus !== 'rejected' && isAwaitingTripStart(shipment)

  const tone: MobileTone = pendingAcceptance
    ? 'warning'
    : assignmentStatus === 'rejected'
      ? 'danger'
    : status === 'in_progress'
      ? 'info'
      : status === 'completed' || status === 'delivered'
        ? 'success'
        : status === 'rejected' || status === 'cancelled'
          ? 'danger'
          : 'warning'

  return {
    id: shipment.id,
    title: routeData?.order_number?.trim()
      ? `Заявка ${routeData.order_number.trim()}`
      : `Заявка #${shipment.id.slice(-6).toUpperCase()}`,
    route: getRouteLabel(shipment),
    status,
    // An invitation has its own lifecycle and must not be presented as a shipment transition.
    statusLabel: assignmentStatus === 'rejected'
      ? 'Отклонена'
      : pendingAcceptance
        ? 'Новая заявка'
        : awaitingStart
          ? 'Рейс не начат'
          : options.managerView ? getManagerStatusLabel(status, shipment.assignment) : translateStatus(status),
    tone,
    timeLabel: formatOrderCompactDate(date),
    timeCaption: routeData?.loading_date ? 'Погрузка' : 'Создана',
    driverLabel: options.managerView
      ? driver || (pendingAcceptance ? 'Требуется ваше решение' : 'Водитель не назначен')
      : '',
    managerLabel: options.managerView ? undefined : managerLabel || undefined,
    managerName: options.managerView ? undefined : managerName || undefined,
    managerCompany: options.managerView ? undefined : managerCompany || undefined,
    vehicleLabel: [plate, vehicleType].filter(Boolean).join(' · ') || 'Машина не назначена',
    vehicleSpecified,
    documentsLabel: documentCount == null
      ? undefined
      : documentsLabel(documentCount),
    documentsCount: documentCount,
    warningLabel: attention?.reason || (pendingAcceptance ? 'Заявка ожидает вашего подтверждения' : undefined),
    // Manager view only: /api/driver/shipments carries no last_location, so on a driver card this
    // always read «Геопозиция не поступала» while the driver's own points were arriving (as the app,
    // the driver sees the transmission state on the trip itself, not on the card).
    lastLocationLabel: options.managerView
      ? getLastTrackingLabel(
        shipment,
        shipment.last_location?.timestamp ? { hasPositions: true, positionCount: 1, lastPositionAt: shipment.last_location.timestamp } : undefined,
        options.now ?? new Date(),
      )
      : undefined,
    managerView: options.managerView,
    canReadInstructions: !options.managerView && !pendingAcceptance && (!assignmentStatus || assignmentStatus === 'accepted'),
    raw: shipment,
    trackingCode: options.trackingCode,
    pendingAcceptance,
    awaitingStart,
  }
}

// The native screen waits for ScrollView's rubber-band offset to reach -72 px. The web receives
// the raw finger travel instead, before that resistance, so it needs a longer path to feel equal.
const PULL_TO_REFRESH_TRIGGER_PX = 128
const PULL_TO_REFRESH_INDICATOR_REVEAL_PX = 24
const PULL_TO_REFRESH_MIN_VISIBLE_MS = 650

function isStandalonePwa() {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function MobileScreen({
  children,
  className = '',
  onRefresh,
}: {
  children: ReactNode
  className?: string
  onRefresh?: () => Promise<unknown> | unknown
}) {
  const [refreshing, setRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const touchStartY = useRef<number | null>(null)
  const triggeredForPull = useRef(false)
  const refreshInFlight = useRef<Promise<void> | null>(null)
  const canRefresh = Boolean(onRefresh) && isStandalonePwa()

  const refresh = useCallback(() => {
    if (!onRefresh || refreshInFlight.current) return refreshInFlight.current

    const startedAt = Date.now()
    setRefreshing(true)
    const request = Promise.resolve()
      .then(onRefresh)
      // Screens retain their own error states. The pull acknowledgement should always settle.
      .catch(() => undefined)
      .then(async () => {
        const remaining = PULL_TO_REFRESH_MIN_VISIBLE_MS - (Date.now() - startedAt)
        if (remaining > 0) await new Promise<void>((resolve) => window.setTimeout(resolve, remaining))
      })
      .finally(() => {
        refreshInFlight.current = null
        setRefreshing(false)
      })
    refreshInFlight.current = request
    return request
  }, [onRefresh])

  const resetPull = () => {
    touchStartY.current = null
    triggeredForPull.current = false
    setPullDistance(0)
  }

  const pullProgress = Math.min(pullDistance / PULL_TO_REFRESH_TRIGGER_PX, 1)
  const showPullIndicator = refreshing || pullDistance >= PULL_TO_REFRESH_INDICATOR_REVEAL_PX

  return (
    <div
      className={`mw-screen${canRefresh ? ' has-pull-to-refresh' : ''} ${className}`.trim()}
      onTouchStart={(event) => {
        if (isPageScrollLocked() || !canRefresh || refreshing || event.touches.length !== 1 || window.scrollY > 0) return
        touchStartY.current = event.touches[0].clientY
        triggeredForPull.current = false
        setPullDistance(0)
      }}
      onTouchMove={(event) => {
        if (isPageScrollLocked()) { resetPull(); return }
        if (!canRefresh || touchStartY.current == null || window.scrollY > 0) return
        const distance = event.touches[0].clientY - touchStartY.current
        if (distance <= 0) { resetPull(); return }
        setPullDistance(distance)
        if (distance >= PULL_TO_REFRESH_TRIGGER_PX && !triggeredForPull.current) {
          triggeredForPull.current = true
          void refresh()
        }
      }}
      onTouchEnd={resetPull}
      onTouchCancel={resetPull}
    >
      {showPullIndicator ? (
        <div
          className={`mw-pull-to-refresh${refreshing ? ' is-refreshing' : ' is-pulling'}${!refreshing && pullProgress === 1 ? ' is-armed' : ''}`}
          role={refreshing ? 'status' : undefined}
          aria-label={refreshing ? 'Обновляем данные' : undefined}
          aria-hidden={refreshing ? undefined : true}
          style={{ opacity: refreshing ? 1 : pullProgress }}
        >
          {refreshing ? <LoaderCircle size={18} /> : <ArrowDown size={18} />}
        </div>
      ) : null}
      {children}
    </div>
  )
}

function MobileHeaderMenuButton() {
  const menu = useMobileMenu()
  if (!menu) return null
  return <button className="mw-icon-button" type="button" onClick={menu.openMenu} aria-label="Открыть меню"><Menu size={20} /></button>
}

export function MobileIntroCard({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle: string
  action?: ReactNode
}) {
  return (
    <header className="mw-intro-card">
      <span className="mw-top-accent" aria-hidden="true" />
      <div className="mw-intro-card__copy">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div className="mw-intro-card__action">{action || <MobileHeaderMenuButton />}</div>
    </header>
  )
}

export function MobileHero({
  artwork,
  eyebrow,
  title,
  subtitle,
  metrics,
  actionLabel,
  onAction,
  actions,
  compact = false,
}: {
  eyebrow: string
  title: string
  subtitle?: string
  metrics: Array<{ label: string; value: string | number; tone?: MobileTone; icon?: ComponentType<{ size?: number }>; onClick?: () => void; ariaLabel?: string }>
  actionLabel: string
  onAction: () => void
  actions?: ReactNode
  compact?: boolean
  artwork?: ReactNode
}) {
  return (
    <header className={`mw-hero${compact ? ' mw-hero--compact' : ''}${artwork ? ' mw-hero--dispatch' : ''}`}>
      <div className={`mw-hero__art${artwork ? ' mw-hero__art--dispatch' : ''}`} aria-hidden="true">
        {artwork ?? <><span /><span /><span /></>}
      </div>
      <div className="mw-hero__top">
        <span className="mw-brand" aria-label="ЛУПА">
          <img className="mw-brand__logo" src="/logolupa2.png" alt="" aria-hidden="true" />
        </span>
        <div className="mw-hero__actions">{actions}<MobileHeaderMenuButton /></div>
      </div>
      <div className="mw-hero__copy">
        <p>{eyebrow}</p>
        <h1>{title}</h1>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      <div className={`mw-hero__metrics${metrics.length === 3 ? ' mw-hero__metrics--three' : ''}`}>
        {metrics.map((metric) => {
          const Icon = metric.icon
          const content = <>{Icon ? <Icon size={16} /> : null}<strong>{metric.value}</strong><span>{metric.label}</span></>
          if (metric.onClick) {
            return (
              <button className={`mw-hero-metric mw-hero-metric--button is-${metric.tone || 'neutral'}`} key={metric.label}
                type="button" onClick={metric.onClick} aria-label={metric.ariaLabel ?? `${metric.label}: ${metric.value}. Открыть трекинг`}>
                {content}
              </button>
            )
          }
          return (
            <div className={`mw-hero-metric is-${metric.tone || 'neutral'}`} key={metric.label}>
              {content}
            </div>
          )
        })}
      </div>
      <button className="mw-primary-button mw-hero__button" type="button" onClick={onAction}>
        <span>{actionLabel}</span><ChevronRight size={18} />
      </button>
    </header>
  )
}

export function MobileSectionHeader({
  eyebrow,
  title,
  actionLabel,
  onAction,
}: {
  eyebrow?: string
  title: string
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div className="mw-section-header">
      <div>
        {eyebrow ? <p>{eyebrow}</p> : null}
        <h2>{title}</h2>
      </div>
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}<ChevronRight size={16} /></button> : null}
    </div>
  )
}

export function MobileSectionCard({
  id,
  title,
  subtitle,
  action,
  children,
  accent = false,
  className = '',
}: {
  id?: string
  title?: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  accent?: boolean
  className?: string
}) {
  return (
    <section id={id} className={`mw-section-card${accent ? ' is-accent' : ''} ${className}`.trim()}>
      {title || subtitle || action ? (
        <div className="mw-section-card__header">
          <div>
            {title ? <h2>{title}</h2> : null}
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export function MobileQuickAction({
  icon: Icon,
  title,
  description,
  onClick,
  accent = false,
}: {
  icon: ComponentType<{ size?: number }>
  title: string
  description: string
  onClick: () => void
  accent?: boolean
}) {
  return (
    <button className={`mw-quick-action${accent ? ' is-accent' : ''}`} type="button" onClick={onClick}>
      <span className="mw-quick-action__icon"><Icon size={21} /></span>
      <strong>{title}</strong>
      <span>{description}</span>
      <ChevronRight className="mw-quick-action__arrow" size={17} />
    </button>
  )
}

export function MobileOrderCard({
  onDocumentsClick,
  order,
  onClick,
  featured = false,
  driverSummary = false,
  trackingActive = false,
  primaryAction,
  secondaryActions = [],
}: {
  order: MobileOrderView
  onDocumentsClick?: () => void
  onClick?: () => void
  featured?: boolean
  /** Driver cards need the dispatcher contacts, not a repeat of the driver's own vehicle. */
  driverSummary?: boolean
  /** The browser is actively uploading this trip's position. */
  trackingActive?: boolean
  primaryAction?: { label: string; onClick: () => void }
  secondaryActions?: Array<{ label: string; onClick: () => void }>
}) {
  const showVehicleOnCard = !order.pendingAcceptance || order.vehicleSpecified
  const compact = Boolean(order.managerView)
  const showDriver = order.driverLabel && (!compact || !order.driverLabel.includes('не назначен'))
  const showVehicle = showVehicleOnCard && (!compact || !order.vehicleLabel.includes('не назначена'))
  const compactMeta = [showVehicle ? order.vehicleLabel : '', showDriver ? order.driverLabel : ''].filter(Boolean).join(' · ')
  const routeParts = order.route.split(' → ')
  const showLocation = order.lastLocationLabel && !compact

  return (
    <article className={`mw-order-card${featured ? ' is-featured' : ''}${compact ? ' is-compact' : ''}`}>
      <div className="mw-order-card__main" role="button" tabIndex={0} onClick={onClick} onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick?.() }
      }}>
        <div className="mw-order-card__art" aria-hidden="true">
          <span className="mw-order-card__road" />
          <MapPin className="mw-order-card__pin" size={23} />
          <Truck className="mw-order-card__truck" size={23} />
        </div>
        <div className="mw-order-card__head">
          <div>
            <h3>{order.title}</h3>
            {!compact ? <p>{order.route}</p> : null}
          </div>
          <span className={`mw-status is-${order.tone}`}>{order.statusLabel}</span>
        </div>
        {compact ? (
          <p className={`mw-order-card__compact-route${routeParts.length === 2 ? ' has-endpoints' : ''}`} aria-label={order.route}>
            {routeParts.length === 2 ? <><span title={routeParts[0]}>{routeParts[0]}</span><span aria-hidden="true">→</span><span title={routeParts[1]}>{routeParts[1]}</span></> : order.route}
          </p>
        ) : null}
        {order.warningLabel ? <div className="mw-order-warning"><CircleAlert size={16} />{order.warningLabel}</div> : null}
        {!compact ? <div className="mw-order-card__meta">
          {driverSummary ? (
            <>
              <span className="mw-order-card__manager" title={order.managerName || undefined}>
                {order.managerName || 'Менеджер не указан'}
              </span>
              <span className="mw-order-card__manager" title={order.managerCompany || undefined}>
                {order.managerCompany || 'Компания не указана'}
              </span>
              <DriverInstructionsBadge shipmentId={order.id} comment={order.raw.driver_comment} canRead={order.canReadInstructions} />
            </>
          ) : order.managerLabel ? (
            <>
              {order.managerView ? (order.documentsLabel ? <span>{order.documentsLabel}</span> : null) : <DriverInstructionsBadge shipmentId={order.id} comment={order.raw.driver_comment} canRead={order.canReadInstructions} />}
              {showVehicleOnCard ? <span className="mw-order-card__transport">{order.vehicleLabel}</span> : null}
              <span className="mw-order-card__manager" title={order.managerLabel}>{order.managerLabel}</span>
            </>
          ) : (
            <>
              {order.driverLabel ? <span className={order.driverLabel.includes('не назначен') ? 'is-attention' : ''}>{order.driverLabel}</span> : null}
              {showVehicleOnCard ? <span className={order.vehicleLabel.includes('не назначена') ? 'is-attention' : ''}>{order.vehicleLabel}</span> : null}
              {order.managerView ? (order.documentsLabel ? <span>{order.documentsLabel}</span> : null) : <DriverInstructionsBadge shipmentId={order.id} comment={order.raw.driver_comment} canRead={order.canReadInstructions} />}
            </>
          )}
        </div> : null}
        {compact && (order.documentsCount ?? 0) > 0 ? (
          <button type="button" className="mw-order-card__documents" aria-label={`Прикреплено документов: ${order.documentsCount}`} onClick={(event) => {
            event.stopPropagation()
            const openDocuments = onDocumentsClick ?? onClick
            openDocuments?.()
          }}>
            <FileText size={14} aria-hidden="true" /><span>Документы: {order.documentsCount}</span>
          </button>
        ) : null}
        <div className="mw-order-card__footer">
          <span><small>{order.timeCaption}</small><strong>{order.timeLabel}</strong></span>
          {compact && compactMeta ? <span className="mw-order-card__compact-meta" title={compactMeta}>{compactMeta}</span> : null}
          {trackingActive ? <span className="mw-order-card__location"><Navigation size={13} />Геопозиция передаётся</span> : null}
          {!trackingActive && showLocation ? <span className="mw-order-card__location"><Navigation size={13} />{order.lastLocationLabel}</span> : null}
          {onClick ? <ChevronRight size={18} /> : null}
        </div>
      </div>
      {primaryAction || secondaryActions.length > 0 ? (
        <div className="mw-order-card__actions">
          {secondaryActions.map((action) => <button key={action.label} type="button" onClick={action.onClick}>{action.label}</button>)}
          {primaryAction ? <button className="is-primary" type="button" onClick={primaryAction.onClick}>{primaryAction.label}</button> : null}
        </div>
      ) : null}
    </article>
  )
}

/** A short-lived, low-noise notice for a trip the driver had already accepted. */
// «Понятно» takes the trip out of «Отменены менеджером» at once; it stays in the history (Никита, 01.11.2026).
export function MobileCancelledOrderCard({
  order,
  onClick,
  onDismiss,
}: {
  order: MobileOrderView
  onClick: () => void
  onDismiss?: () => void
}) {
  return (
    <div className="mw-cancelled-order">
      <button className="mw-cancelled-order__main" type="button" onClick={onClick}>
        <span className="mw-cancelled-order__icon"><CircleX size={18} /></span>
        <span className="mw-cancelled-order__copy">
          <strong>{order.route || order.title}</strong>
          <small>Отменена менеджером</small>
        </span>
        {onDismiss ? null : <ChevronRight size={18} aria-hidden="true" />}
      </button>
      {onDismiss ? (
        <button
          className="mw-cancelled-order__dismiss"
          type="button"
          onClick={onDismiss}
          title="Заявка уйдёт из этого блока и останется в истории"
        >
          Понятно
        </button>
      ) : null}
    </div>
  )
}

export function MobileFilterBar({
  items,
  selected,
  onChange,
}: {
  items: Array<{ key: string; label: string; count?: number }>
  selected: string
  onChange: (key: string) => void
}) {
  return (
    <div className="mw-filter-scroll" aria-label="Фильтры">
      {items.map((item) => (
        <button className={`mw-filter${selected === item.key ? ' is-selected' : ''}`} key={item.key} type="button" onClick={() => onChange(item.key)}>
          {item.label}{item.count == null ? null : <span>{item.count}</span>}
        </button>
      ))}
    </div>
  )
}

export function MobileState({
  title,
  description,
  loading = false,
  tone = 'neutral',
  actionLabel,
  onAction,
}: {
  title: string
  description: string
  loading?: boolean
  tone?: MobileTone
  actionLabel?: string
  onAction?: () => void
}) {
  const showText = useLoadingText(loading, `${title}\0${description}`)
  return (
    <div className={`mw-state is-${tone}`} aria-busy={loading || undefined} aria-label={loading ? 'Загрузка' : undefined}>
      <span className="mw-state__icon">{loading ? <LoaderCircle className="mw-spin" size={24} /> : <CircleAlert size={23} />}</span>
      {!loading ? <h2>{title}</h2> : null}
      {!loading || showText ? <p>{description}</p> : null}
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  )
}

type MobileSheetProps = {
  open: boolean
  title: string
  subtitle?: string
  headerLeading?: ReactNode
  headerActions?: ReactNode
  children: ReactNode
  onClose: () => void
  className?: string
  /** The child owns its scrolling, for example a form with a pinned submit button. */
  scrollable?: boolean
}

export function MobileSheet({ open, ...props }: MobileSheetProps) {
  usePageScrollLock(open)
  return open ? <MobileSheetDialog {...props} /> : null
}

function MobileSheetDialog({
  title,
  subtitle,
  headerLeading,
  headerActions,
  children,
  onClose,
  className = '',
  scrollable = true,
}: Omit<MobileSheetProps, 'open'>) {
  const panelRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const gesture = useRef<{ id: number; x: number; y: number; distance: number } | null>(null)
  const [offset, setOffset] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    const previousFocus = document.activeElement
    closeButtonRef.current?.focus({ preventScroll: true })
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected && !previousFocus.closest('[inert]')) {
        previousFocus.focus({ preventScroll: true })
      }
    }
  }, [])

  useEffect(() => {
    if (!dismissing) return
    const timeout = window.setTimeout(() => {
      onClose()
      // Some callers refuse to close while a payment or another action is pending.
      setDismissing(false)
      setOffset(0)
    }, window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 180)
    return () => window.clearTimeout(timeout)
  }, [dismissing, onClose])

  const cancelDrag = () => {
    gesture.current = null
    setDragging(false)
    setOffset(0)
  }

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dismissing || gesture.current || !event.isPrimary || event.button !== 0) return
    // In particular, leave the close button and notification menu untouched.
    if ((event.target as Element).closest('button, a, input, select, textarea, [role="button"], [contenteditable="true"]')) return
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, distance: 0 }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gesture.current
    if (!current || current.id !== event.pointerId) return
    const dy = event.clientY - current.y
    if (Math.abs(event.clientX - current.x) > Math.max(8, Math.abs(dy))) {
      cancelDrag()
      return
    }
    current.distance = Math.max(0, dy)
    if (current.distance < 8 && !dragging) return
    setDragging(true)
    setOffset(current.distance)
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gesture.current
    if (!current || current.id !== event.pointerId) return
    const height = panelRef.current?.getBoundingClientRect().height || window.innerHeight
    const shouldClose = current.distance >= Math.min(120, height * 0.3)
    gesture.current = null
    setDragging(false)
    if (shouldClose) {
      setOffset(Math.max(height, current.distance))
      setDismissing(true)
    } else {
      setOffset(0)
    }
  }

  return (
    <div className={`mw-sheet ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <section ref={panelRef} className={`mw-sheet__panel${dragging ? ' is-dragging' : ''}`} style={{ transform: `translateY(${offset}px)` }} onClick={(event) => event.stopPropagation()}>
        <div className="mw-sheet__drag-area"
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={(event) => { if (gesture.current?.id === event.pointerId) cancelDrag() }}
          onLostPointerCapture={(event) => { if (gesture.current?.id === event.pointerId) cancelDrag() }}
        >
          <div className="mw-sheet__grabber" />
          <header className={`mw-sheet__header${headerLeading ? ' mw-sheet__header--with-leading' : ''}`}>
            {headerLeading}
            <div className="mw-sheet__heading"><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
            <div className="mw-sheet__header-actions">
              {headerActions}
              <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Закрыть"><X size={20} /></button>
            </div>
          </header>
        </div>
        <div className={`mw-sheet__content${scrollable ? '' : ' mw-sheet__content--fixed'}`}>{children}</div>
      </section>
    </div>
  )
}

export function MobileBackButton({ onClick }: { onClick: () => void }) {
  return <button className="mw-icon-button" type="button" onClick={onClick} aria-label="Назад"><ChevronLeft size={21} /></button>
}
